import { useRef, forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import clsx from 'clsx'
import { useTheme } from '@emotion/react'
import { rgba } from 'polished'
import Wave from 'react-wavify'
import {
  Section,
  Container,
  SectionTitle,
  Button,
  List,
  ListItem,
} from 'chaoskit/src/components'
import { text } from 'chaoskit/src/assets/styles/utility'
import { motion } from 'framer-motion'
import { Tabs, TabList, Tab, TabPanel, TabPanels } from '@reach/tabs'
import { StylesButtonReset } from 'chaoskit/src/components/Button'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import {
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import Icon from '~components/Icon'
import { stickyIt, fadeInUp } from '~styles/utility'
import Image from '~components/Image'
import waveTop from '~media/chaos-engineering/wave-top.svg'
import { ReactComponent as HeroSVG } from '~media/chaos-engineering/hero.svg'
import graph from '~media/chaos-engineering/graph.svg'
import iceberg from '~media/chaos-engineering/iceberg-main.svg'
import ship from '~media/chaos-engineering/iceberg-ship.svg'
import gremlinIceberg from '~media/chaos-engineering/iceberg-aside.svg'
import useReveal from '~hooks/useReveal'
import cloudinaryTransform from '~helpers/cloudinary'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'

const StylesChaosEngineeringVariables = {
  // Color palette is one-off until design/development meet to standardize on new brand colors
  color: {
    base: '#0f1436',
    light: '#2d327e',
    contrast: '#acb8dc',
    filter:
      'brightness(0) saturate(100%)  invert(6%) sepia(64%) saturate(2205%) hue-rotate(221deg) brightness(99%) contrast(99%)',
  },
}

const ContentSection = forwardRef(({ contrast, className, ...rest }, ref) => {
  const theme = useTheme()

  return (
    <Section
      ref={ref}
      css={[
        {
          a: [text.underline],
        },

        contrast && {
          background: StylesChaosEngineeringVariables.color.base,
        },
      ]}
      className={clsx(className, {
        [theme.settings.classes.contrast]: contrast,
      })}
      {...rest}
    />
  )
})

ContentSection.propTypes = {
  contrast: PropTypes.bool,
  className: PropTypes.string,
}

const ContentGrid = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        position: 'relative', // Used for sticky headers
        gap: theme.space.large,

        [theme.mq.medium]: {
          gridTemplateAreas: `"header ."
                              "aside main"`,
          gridTemplateColumns: '1fr 1.25fr',
          gap: theme.space.xlarge,
        },
      }}
      {...props}
    />
  )
}

const ContentHeader = ({ sticky, ...rest }) => {
  const theme = useTheme()

  return (
    <header
      css={{
        [theme.mq.medium]: [
          {
            gridArea: 'header',
          },

          sticky && [stickyIt(theme)],
        ],
      }}
      {...rest}
    />
  )
}

ContentHeader.propTypes = {
  sticky: PropTypes.bool,
}

const ContentHeaderTitle = (props) => {
  const theme = useTheme()

  return (
    <SectionTitle
      as="h2"
      align={{ medium: 'left' }}
      css={{
        '.CK__SectionTitle__Header': {
          color: StylesChaosEngineeringVariables.color.light,

          '.u-contrast &': {
            color: theme.contrast.base,
          },
        },
      }}
      {...props}
    />
  )
}

const ContentAside = (props) => {
  const theme = useTheme()

  return (
    <aside
      css={{
        [theme.mq.medium]: [
          {
            gridArea: 'aside',
          },
        ],
      }}
      {...props}
    />
  )
}

const ContentBlurb = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        fontSize: theme.fontSize.large,
        color: StylesChaosEngineeringVariables.color.light,
      }}
      {...props}
    />
  )
}

const ContentMain = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        [theme.mq.medium]: {
          gridArea: 'main',
        },
      }}
      {...props}
    />
  )
}

const TestingEvolvedItem = ({ data }) => {
  const theme = useTheme()

  return (
    <ListItem>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '80px 1fr',
          gap: theme.space.large,
        }}
      >
        <Image src={data.image} css={{ maxHeight: 65 }} />
        <div>
          <div
            css={{
              fontSize: theme.fontSize.large,
              color: StylesChaosEngineeringVariables.color.contrast,
              fontWeight: theme.fontWeight.bold,
            }}
          >
            {data.heading}
          </div>
          {data.blurb}
        </div>
      </div>
    </ListItem>
  )
}

TestingEvolvedItem.propTypes = {
  data: PropTypes.object.isRequired,
}

const WhatIsChaosEngineering = ({ data, ...rest }) => {
  const theme = useTheme()
  const ref = useRef()
  const imageRef = useRef()

  const { variants: contentVariants, controls: contentControls } = useReveal({
    ref,
  })

  const { variants: imageVariants, controls: imageControls } = useReveal({
    ref: imageRef,
  })

  return (
    <ContentSection
      ref={ref}
      initial="hidden"
      animate={contentControls}
      variants={contentVariants}
      {...rest}
    >
      <Container>
        <ContentGrid>
          <ContentHeader>
            <ContentHeaderTitle title={<Data>{data.heading}</Data>} />
          </ContentHeader>
          <ContentAside>
            <ContentBlurb
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            >
              <MDXRenderer>{data.subContent}</MDXRenderer>
            </ContentBlurb>
          </ContentAside>
          <ContentMain>
            <MDXRenderer>{data.content}</MDXRenderer>
          </ContentMain>
        </ContentGrid>
        <Container
          size="small"
          css={{
            marginTop: theme.space.xlarge,

            [theme.mq.medium]: {
              marginTop: theme.space.xlarge * 2,
            },
          }}
        >
          <Image
            ref={imageRef}
            initial="hidden"
            variants={imageVariants}
            animate={imageControls}
            src={graph}
          />
        </Container>
      </Container>
    </ContentSection>
  )
}

WhatIsChaosEngineering.propTypes = {
  data: PropTypes.object.isRequired,
}

const ChaosEngineeringTab = (props) => {
  const { isSelected } = props

  const theme = useTheme()

  return (
    <Tab
      css={[
        StylesButtonReset,
        {
          fontWeight: theme.fontWeight.bold,
          fontSize: theme.fontSize.xsmall,
          color: theme.fontColor.muted,
          position: 'relative',
          textAlign: 'center',
        },

        isSelected && {
          color: StylesChaosEngineeringVariables.color.base,
        },
      ]}
      {...props}
    />
  )
}

ChaosEngineeringTab.propTypes = {
  isSelected: PropTypes.bool,
}

const DevOpsTraditionalTesting = ({ data, ...rest }) => {
  const theme = useTheme()
  const ref = useRef()

  const { controls, variants } = useReveal({ ref })

  const [activeIndex, setActiveIndex] = useState(0)

  const handleTabChange = (index) => {
    setActiveIndex(index)
  }

  return (
    <ContentSection
      ref={ref}
      css={{ paddingTop: '0 !important' }}
      initial="hidden"
      animate={controls}
      variants={variants}
      {...rest}
    >
      <Container>
        <ContentGrid>
          <ContentHeader>
            <ContentHeaderTitle title={<Data>{data.heading}</Data>} />
          </ContentHeader>
          <ContentAside>
            <div
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            >
              {data.items && (
                <Tabs index={activeIndex} onChange={handleTabChange}>
                  <TabPanels>
                    {data.items.map((item, index) => (
                      <TabPanel
                        key={item.heading}
                        css={[
                          {
                            textAlign: 'center',
                          },

                          activeIndex === index && {
                            animationName: fadeInUp(theme),
                            animationDuration: theme.timing.base,
                            animationTimingFunction: theme.transition.base,
                          },
                        ]}
                      >
                        <div
                          css={{
                            height: 250,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'contain',
                            backgroundPosition: 'center center',
                          }}
                          style={{
                            backgroundImage: `url(${cloudinaryTransform(
                              item.image
                            )})`,
                          }}
                        />
                      </TabPanel>
                    ))}
                  </TabPanels>
                  <TabList
                    css={{
                      display: 'grid',
                      gridAutoFlow: 'column',
                      gridAutoColumn: 'auto',
                      gap: theme.space.xsmall,
                      listStyle: 'none',
                      alignItems: 'center',
                      padding: 0,
                      margin: 0,
                      marginTop: theme.space.large,
                    }}
                  >
                    {data.items.map((item) => (
                      <ChaosEngineeringTab key={item.heading}>
                        <Data>{item.heading}</Data>
                      </ChaosEngineeringTab>
                    ))}
                  </TabList>
                </Tabs>
              )}
            </div>
          </ContentAside>
          <ContentMain>
            <MDXRenderer>{data.content}</MDXRenderer>
          </ContentMain>
        </ContentGrid>
      </Container>
    </ContentSection>
  )
}

DevOpsTraditionalTesting.propTypes = {
  data: PropTypes.object.isRequired,
}

const IcebergMain = (props) => {
  const theme = useTheme()

  return (
    <Image
      loading="eager"
      src={iceberg}
      css={{
        maxHeight: 'calc(var(--ce__iceberg-size) / 1.5)',

        [theme.mq.medium]: {
          maxHeight: 'var(--ce__iceberg-size)',
          transform: 'translate(-25%, calc(var(--ce__iceberg-size) / 1.5))',
        },
      }}
      {...props}
    />
  )
}

const IcebergContainer = (props) => {
  const theme = useTheme()

  const variants = {
    down: {
      y: 0,
    },
    up: {
      y: '5%',
    },
  }

  return (
    <div
      css={{
        position: 'relative',

        [theme.mq.medium]: {
          gridColumn: 1,
          gridRow: 1,
          alignSelf: 'end',
        },
      }}
      {...props}
    >
      <IcebergMain />
      <Image
        loading="eager"
        src={ship}
        initial="down"
        animate="up"
        variants={variants}
        transition={{
          yoyo: Infinity,
          duration: 2,
        }}
        css={{
          '--ce__ship-size': '180px',

          position: 'absolute',
          left: '60%',
          bottom: '25%',
          height: 'calc(var(--ce__ship-size) / 2)',
          zIndex: 2,

          [theme.mq.medium]: {
            height: 'var(--ce__ship-size)',
            left: '40%',
            bottom: '-45%',
          },
        }}
      />
      <Image
        loading="eager"
        src={gremlinIceberg}
        css={{
          '--ce__gremlin-size': '140px',

          position: 'absolute',
          bottom: '40%',
          right: 0,
          height: 'calc(var(--ce__gremlin-size) / 1.5)',

          [theme.mq.medium]: {
            bottom: '-25%',
            height: 'var(--ce__gremlin-size)',
          },
        }}
      />
    </div>
  )
}

const TraditionalQANotEnough = ({ data, ...rest }) => {
  const theme = useTheme()
  const ref = useRef()

  const { controls, variants } = useReveal({ ref })

  return (
    <ContentSection
      ref={ref}
      animate={controls}
      variants={variants}
      initial="hidden"
      css={{
        overflow: 'hidden',
        paddingBottom: '0 !important',
      }}
      {...rest}
    >
      <div
        css={{
          [theme.mq.medium]: {
            gridArea: 'main',
          },
        }}
      >
        <Container>
          <div
            css={{
              '--ce__iceberg-size': '800px',

              display: 'grid',
              alignItems: 'start',
              gap: theme.space.large,

              [theme.mq.medium]: {
                gridTemplateColumns: 'auto',
                paddingBottom: `calc(var(--ce__iceberg-size) / 2)`,
              },
            }}
          >
            <ContentGrid
              css={{
                gridColumn: 1,
                gridRow: 1,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <ContentHeader>
                <ContentHeaderTitle title={<Data>{data.heading}</Data>} />
              </ContentHeader>
              <ContentAside>
                <ContentBlurb>
                  <Data>{data.blurb}</Data>
                </ContentBlurb>
              </ContentAside>
              <ContentMain>
                <MDXRenderer>{data.content}</MDXRenderer>
              </ContentMain>
            </ContentGrid>
            <IcebergContainer />
          </div>
        </Container>
        <div css={{ marginTop: -150, position: 'relative', zIndex: 1 }}>
          <Wave
            fill={StylesChaosEngineeringVariables.color.base}
            options={{
              height: 20,
              amplitude: 30,
              speed: 0.25,
              points: 4,
            }}
          />
          <Wave
            fill={rgba(
              StylesChaosEngineeringVariables.color.base,
              theme.opacity.base
            )}
            options={{
              height: 20,
              amplitude: 20,
              speed: 0.15,
              points: 3,
            }}
            css={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: -1,
            }}
          />
        </div>
      </div>
    </ContentSection>
  )
}

TraditionalQANotEnough.propTypes = {
  data: PropTypes.object.isRequired,
}

const HowDoesChaosEngineeringEvolveTesting = ({ data, ...rest }) => {
  const theme = useTheme()
  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <ContentSection contrast {...rest}>
      {/* Putting this inside of a child instead of the `ContentSection` since we want the background to still show when not visible */}
      <motion.div
        ref={ref}
        variants={variants}
        animate={controls}
        initial="hidden"
      >
        <Container>
          <ContentGrid>
            <ContentHeader>
              <ContentHeaderTitle title={<Data>{data.heading}</Data>} />
            </ContentHeader>
            <ContentAside>
              <div
                css={{
                  [theme.mq.medium]: [stickyIt(theme)],
                }}
              >
                <List space="large">
                  {data.items.map((item) => (
                    <TestingEvolvedItem key={item.heading} data={item} />
                  ))}
                </List>
              </div>
            </ContentAside>
            <ContentMain>
              <MDXRenderer>{data.content}</MDXRenderer>
            </ContentMain>
          </ContentGrid>
        </Container>
      </motion.div>
    </ContentSection>
  )
}

HowDoesChaosEngineeringEvolveTesting.propTypes = {
  data: PropTypes.object.isRequired,
}

const GetStartedWithChaosEngineering = ({ data, ...rest }) => {
  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <ContentSection contrast {...rest}>
      {/* Putting this inside of a child instead of the `ContentSection` since we want the background to still show when not visible */}
      <motion.div
        ref={ref}
        variants={variants}
        animate={controls}
        initial="hidden"
      >
        <Container>
          <ContentGrid>
            <ContentHeader sticky>
              <ContentHeaderTitle title={<Data>{data.heading}</Data>} />
            </ContentHeader>
            <ContentMain>
              <MDXRenderer>{data.content}</MDXRenderer>
            </ContentMain>
          </ContentGrid>
        </Container>
      </motion.div>
    </ContentSection>
  )
}

GetStartedWithChaosEngineering.propTypes = {
  data: PropTypes.object.isRequired,
}

const ChaosEngineering = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout footerProps={{ hideCTA: true }}>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <NodeTypeTracker nodeType="microsite" />
      <Hero
        css={{
          background: StylesChaosEngineeringVariables.color.base,

          [theme.mq.medium]: {
            '&::before': {
              content: "''",
              position: 'absolute',
              bottom: -1,
              left: 0,
              backgroundImage: `url(${waveTop})`,
              backgroundRepeat: 'repeat-x',
              backgroundPosition: 'left bottom',
              backgroundSize: '2807px 221px', // Exact illustration dimensions
              height: '100%',
              width: '2807px',
              zIndex: 0,
            },
          },
        }}
      >
        <HeroContent>
          <HomeHeroGrid
            css={{
              alignItems: 'start',

              [theme.mq.medium]: {
                paddingBottom: theme.space.xlarge,
              },
            }}
          >
            <HomeHeroGridImage
              css={{
                [theme.mq.small__max]: {
                  textAlign: 'center',

                  svg: {
                    maxWidth: 400,
                  },
                },

                [theme.mq.medium]: {
                  svg: {
                    height: 450,
                  },
                },
              }}
            >
              <HeroSVG />
            </HomeHeroGridImage>
            <HomeHeroGridContent>
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
            </HomeHeroGridContent>
          </HomeHeroGrid>
        </HeroContent>
      </Hero>
      <WhatIsChaosEngineering data={pageData.whatIsChaosEngineering} />
      <DevOpsTraditionalTesting data={pageData.devOpsTraditionalTesting} />
      <TraditionalQANotEnough data={pageData.traditionalQANotEnough} />
      <HowDoesChaosEngineeringEvolveTesting
        data={pageData.howDoesChaosEngineeringEvolveTesting}
        css={{ position: 'relative', zIndex: 1 }} // To sit on-top of iceberg
      />
      <GetStartedWithChaosEngineering
        data={pageData.getStartedWithChaosEngineering}
      />
      <Button
        type="primary"
        size="small"
        css={{
          position: 'fixed',
          bottom: theme.space.base,
          right: theme.space.base,
          boxShadow: theme.boxShadow.base,
          transition: `transform ${theme.timing.base} ${theme.transition.base}`,
          transformOrigin: 'center center',
          zIndex: 3,

          '&:hover, &:focus': {
            transform: 'scale(1.05)',
          },
        }}
        url="/the-new-qa/"
      >
        Upgrade your testing
        <Icon css={{ marginLeft: theme.space.small }} icon="arrow-right" />
      </Button>
    </BaseLayout>
  )
}

ChaosEngineering.propTypes = {
  data: PropTypes.object.isRequired,
}

export default ChaosEngineering

export const pageQuery = graphql`
  query ChaosEngineeringPageData {
    file(
      name: { eq: "chaos-engineering" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            heading
            blurb
          }
          whatIsChaosEngineering {
            heading
            subContent
            content
          }
          devOpsTraditionalTesting {
            heading
            content
            items {
              heading
              image
            }
          }
          traditionalQANotEnough {
            heading
            blurb
            content
          }
          howDoesChaosEngineeringEvolveTesting {
            heading
            items {
              heading
              blurb
              image
            }
            content
          }
          getStartedWithChaosEngineering {
            heading
            content
          }
        }
      }
    }
  }
`
